import type { JSX, ReactNode } from 'react'
import type { ApiFailure } from '@/api/client'
import { InlineError } from '@/ui/states'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { STEP_META } from './steps'
import type { StepKey } from './steps'

/**
 * The chrome around a settings step: meter, title, consequence, the step's
 * form, and a footer with Back and Skip — the form owns Continue.
 *
 * Settings consolidation (2026-09-15). Five steps share this so the only thing
 * each one writes is which query it reads and which form it renders.
 */
export function SettingsStepShell({
  step,
  failure,
  rail = true,
  offerSkip = true,
  children,
}: {
  step: StepKey
  /** From `useSettleStep` — the done call refused, after the value saved. */
  failure: ApiFailure | null
  rail?: boolean
  /**
   * ONB-19 for the last step: *Finish setup* saves whatever the controls hold,
   * all of which have defaults, so a Skip beside it offers the same outcome
   * under a word that suggests something was given up.
   */
  offerSkip?: boolean
  children: ReactNode
}): JSX.Element {
  const meta = STEP_META[step]
  return (
    <div className="app-wiz">
      <WizardChrome current={step} rail={rail} />
      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">{meta.title}</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              {meta.consequence} You can change this any time under Settings.
            </p>
          </div>
          {children}
          <InlineError failure={failure} />
        </div>
        {/* The form's own submit is the step's action, so the footer carries
            only Back and Skip. */}
        <WizardFooter current={step} offerSkip={offerSkip}>
          {null}
        </WizardFooter>
      </section>
    </div>
  )
}

/** What a per-site step shows when the wizard's site is gone. */
export function NoSiteYet(): JSX.Element {
  return (
    <p className="ar-muted" style={{ margin: 0 }}>
      Add a site first — this step is about a site, and setup has none yet. Skip for now and set it
      under Settings once a site exists.
    </p>
  )
}
